import { OpenInNewRounded } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { Fragment, useMemo } from 'react';

import { useFetchZoneLinksQuery } from 'redux-rtk';
import { theme } from '~/theme';
import { getZoneIdentifier, getZoneTag } from '~/utils';

import { I18n } from '../../../i18n';
import { Card } from '../../../layouts/Card';
import { Divider } from '../../Divider';
import { Zone } from '../../Map/types';
import { Icon } from '../../UI/Icon';
import { Title } from '../shared/Title';
import { ZoneBoundaries } from './ZoneBoundaries';
import { ZoneWeather } from './ZoneWeather';

interface MiscZoneInfoProps {
  zone: Zone;
  customZones?: Zone[];
}

export const MiscZoneInfo = ({ zone, customZones }: MiscZoneInfoProps) => {
  const zoneIdentifiers = useMemo(() => {
    const identifiers = [];
    if (zone?.identifier) {
      identifiers.push(zone?.identifier);
    }
    customZones?.forEach?.(zone => {
      identifiers.push(zone.identifier);
    });
    return identifiers;
  }, [customZones, zone?.identifier]);
  const { data: links = [] } = useFetchZoneLinksQuery(zoneIdentifiers);

  const communityLinks: { [key: string]: { alerts: string; website: string } } =
    useMemo(() => {
      let _communityLinks = [zone, ...(customZones || [])].reduce(
        (_links, _zone) => {
          // If we have data and don't yet have it in the links
          if (
            (_zone?.countyAlerts || _zone?.countyWebsite) &&
            !(_zone.countyName in _links)
          ) {
            // Add them
            _links[_zone.countyName] = {
              alerts: _zone.countyAlerts,
              website: _zone.countyWebsite,
            };
          }
          return _links;
        },
        {},
      );
      return _communityLinks;
    }, [customZones, zone]);

  return (
    <Card hasShadow={false} backgroundColor="grey">
      <Title
        title={I18n.t('zoneDetails.locationInformation')}
        icon={<Icon size={18} name="zone-info" isDecorative={true} />}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: theme.spacing(0.5),
        }}
      >
        <Typography
          variant="body2"
          textTransform={'uppercase'}
          fontWeight={'800'}
          sx={{
            fontFamily: 'Roboto',
          }}
        >
          {I18n.t('zoneDetails.zoneBoundaries')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            display: 'flex', // keep it inline but allow layout control
            alignItems: 'center',
          }}
        >
          (
          <Box
            component="span"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexShrink: 1,
              minWidth: 0,
            }}
          >
            {getZoneTag(zone?.layerName, I18n.t)}:{' '}
            {getZoneIdentifier({
              layerName: zone?.layerName,
              identifier: zone?.identifier,
            })}
          </Box>
          )
        </Typography>
      </Box>
      <ZoneBoundaries
        zoneBoundaries={zone?.boundaries?.en}
        zoneName={zone?.commonlyknownas}
      />

      <Typography
        variant="body2"
        textTransform={'uppercase'}
        fontWeight={'800'}
        sx={{
          fontFamily: 'Roboto',
          marginTop: theme.spacing(2),
        }}
      >
        {I18n.t('zoneDetails.currentWeather')}
      </Typography>
      <ZoneWeather
        sx={{ mt: 2 }}
        location={[zone?.latitude, zone?.longitude]}
      />

      {Object.keys(communityLinks).length > 0 && (
        <Fragment>
          <Divider sx={{ my: 3 }} />

          <Title title={I18n.t('zoneDetails.communityLinks')} sx={{ mb: 1 }} />

          {Object.entries(communityLinks).map(([county, countyLinks]) => (
            <>
              <Typography variant="body2" sx={{ mb: 4 }}>
                {I18n.t('zoneDetails.followSources', { name: county })}
              </Typography>

              <Box
                sx={{
                  mt: 3,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-around',
                }}
              >
                {/* TODO: Add Twitter URL when available */}
                {/* <a
              className="link"
              target={"_blank"}
              rel="noreferrer"
              href={zone.countyWebsite}
            >
              <Icon size={20} name={"twitter"} />
              <span>Twitter</span>
            </a> */}

                {countyLinks.website && (
                  <a
                    className="link"
                    target={'_blank'}
                    rel="noreferrer"
                    href={countyLinks.website}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon size={20} name={'web'} isDecorative={true} />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        {I18n.t('zoneDetails.website')}
                      </Typography>
                    </Box>
                  </a>
                )}

                {countyLinks.alerts && (
                  <a
                    className="link"
                    target={'_blank'}
                    rel="noreferrer"
                    href={countyLinks.alerts}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon size={20} name={'alerts'} isDecorative={true} />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        {I18n.t('zoneDetails.alerts')}
                      </Typography>
                    </Box>
                  </a>
                )}
              </Box>
            </>
          ))}
        </Fragment>
      )}

      {links.length > 0 && (
        <Fragment>
          <Divider sx={{ my: 3 }} />
          <Title title={'Other links'} />

          <Box
            sx={{
              mt: 3,
              gap: [3, 3, 2],
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {links.map(link => (
              <a
                key={link?.url}
                className="link"
                target={'_blank'}
                rel="noreferrer"
                href={link?.url}
              >
                <Box
                  sx={{
                    gap: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <OpenInNewRounded
                    sx={{ height: 16, width: 16 }}
                    role="img"
                    titleAccess="Opens in a new tab"
                  />
                  <Typography variant="body1">{link?.name}</Typography>
                </Box>
              </a>
            ))}
          </Box>
        </Fragment>
      )}
    </Card>
  );
};
